import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {Sorting} from 'features/core/shared/misc/sorting';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {ProvisionRunDto} from '../../../model/dto/entity/provision-run.dto';
import {ProvisionRunStatus} from '../../../enum/provision-run-status.enum';
import {
    CpRunStatusSeverity,
    getProvisionRunStatusLabel,
    getProvisionRunStatusSeverity,
    isProvisionRunProcessing
} from '../../../enum/provision-run-status-label';
import {CpProvisionRunService} from '../../../service/api/cp-provision-run.service';
import {buildCpWidgetForm} from '../../../service/domain/cp-widget-column';
import {buildCpRunWidgetForm} from '../../../service/domain/cp-run-widget-column';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';
import {ProvisionContractRunStatus} from '../../../enum/provision-contract-run-status.enum';
import {ProvisionMessageSeverity} from '../../../enum/provision-message-severity.enum';
import {ContractAgentEnum} from 'features/feature-crm/cm/enum/contract-agent-enum.enum';
import {CpRunContractFilter} from '../../../interface/cp-run-contract-filter.interface';
import {CpRunMessageFilter} from '../../../interface/cp-run-message-filter.interface';
import {CpRunFilterOption} from '../../../interface/cp-run-filter-option.interface';
import {CpRunTabKey} from '../../../interface/cp-run-tab-key.interface';
import {CpRunEntityTabKey} from '../../../interface/cp-run-entity-tab-key.interface';
import {CpRunTabSelection} from '../../../interface/cp-run-tab-selection.interface';
import {CpRunTabCounts} from '../../../interface/cp-run-tab-counts.interface';

/**
 * Detail view of a Provisionslauf — route `cp/dashboard/run/:provisionRunId`
 * (`/#/cp/dashboard/run/{id}`), 04-frontend.md §5.
 *
 * Built in the form of the repo original `BmBillingRunDetailsComponent`
 * (`features/feature-bm/bm/component/protected-components/bm-billing-run-details-component/`):
 * a run header, a `p-tabs` band, an "Übersicht" tab carrying the step bar, and one tab per entity
 * produced by the run — with the run RE-READ ON EVERY TAB CHANGE, because all of this data is
 * produced WHILE the jobs run, so anything read once at page load is stale by the time the operator
 * looks at it (the same reason bm writes down at its own `tabChange()`).
 *
 * ── THE SIX ENTITY TABS ARE REVIEW WORKBENCHES (04 §5.3, PAT §K, skill fe_core_review-workbench)
 *
 * Each entity tab is a `mvs-review-workbench`: left the QL table widget filtered on this run, right
 * `<mvs-object>` rendering the selected row in object mode. The three hard rules of the pattern:
 *
 *   R1  the SHARED component — never a hand-rolled `p-splitter` plus viewport SCSS,
 *   R2  exactly ONE `#workbenchList` instantiation site per tab; the worklist widget is built ONCE
 *       (in {@link initTabWidgets}) and reloaded IN PLACE through the `MvsWidgetComponent` refs
 *       below — NEVER rebuilt inside a selection handler, which would throw away the reviewer's
 *       sort/filter/scroll on every single row click,
 *   R3  pane visibility runs through `[panes]="selection.x ? 2 : 1"` ONLY — never an `@if` around
 *       the workbench. PrimeNG's Splitter builds its panel array once in `onAfterContentInit`, so a
 *       structural toggle would destroy the projected worklist view and re-mount the table.
 *
 * ── HOW A TAB LOADS LAZILY WITHOUT EVER RE-MOUNTING ITS WORKLIST
 *
 * Two things must be true at once and they pull in opposite directions: a tab must not query
 * anything before it is opened, and its worklist must never be destroyed once it exists.
 *
 * The bm original gates each panel body on `@if (activeIndex == n)`, which buys the first at the
 * cost of the second: the gate DESTROYS the panel content on every tab switch, taking the workbench
 * and its worklist with it, so the table re-mounts and re-fetches — exactly what R2 forbids.
 * PrimeNG's `[lazy]` on a tab panel does not solve it either: the panel renders `<ng-content>`
 * inside an `<ng-template>`, and Ivy instantiates PROJECTED content eagerly in the parent view, so
 * `lazy` would only hide the DOM unless every panel were additionally wrapped in the component's
 * `<ng-template #content>` escape hatch (which is precisely why PrimeNG documents that template as
 * "for initializing complex content when lazy is enabled").
 *
 * So the gate sits one level lower, on the WIDGET rather than on the panel: the workbench of every
 * tab is mounted from the start (empty, cheap, and — importantly — structurally stable), and a tab's
 * `WidgetData` is built by {@link ensureTabWidget} the first time that tab is opened. Before that
 * the `@if (…Widget)` inside `#workbenchList` renders nothing and no request is made; afterwards the
 * reference is NEVER reassigned, so the table is created exactly once and is only ever reloaded in
 * place. First open = one query, every later switch = zero, and no remount at any point.
 *
 * ── THE DETAIL PANES ARE READ-ONLY (FE-A, round 1 of the business review)
 *
 * A run review must never change what the engine produced, so no tab opens an editable form:
 *   - Verträge            -> `cp-run-detail-contract-run` (outcome, Fehlergrund + "Was tun?",
 *                            Hauptvermittler, bookings with sum),
 *   - Meldungen / Lauf-Meldungen -> `cp-run-detail-message` (guidance, detail, contract + account),
 *   - transactions, Deckung -> `mvs-crud-object` with `readonly` + `hideActionBar` (no save/delete),
 *   - Ereignisse / Sätze  -> their own OBCs (display widgets) plus `cp-run-detail-contract-link`.
 * All of them read via QL or a GET; none of them can reach an update, a delete or a form-template
 * refresh.
 *
 * ── HEIGHT
 *
 * `autoFitViewport` is bound PER TAB (`activeTab === key`), so exactly one workbench runs the
 * measurement machinery and it re-measures the moment its tab becomes visible — a workbench inside a
 * hidden panel would otherwise measure a top edge of 0 (the component's own hidden-host guard skips
 * it, but then it also holds no value). `viewportOffsetPx` is passed purely as the documented
 * pre-measurement fallback, exactly as the three bm reconciliation hosts pass their 152: the chrome
 * above a tab panel (breadcrumb + page header + run header + tab band) is not constant, so no single
 * constant is correct and auto-fit measures instead of guessing.
 */
@Component({
    selector: 'cp-provision-run-detail',
    templateUrl: './cp-provision-run-detail.component.html',
    styleUrls: ['./cp-provision-run-detail.component.scss'],
    standalone: false
})
export class CpProvisionRunDetailComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * QL alias of the run-scope reverse join - fixed so tests can name it. The literal `'rcr'` is
     * written inline in the queries (single-assignment rule); the spec asserts the two agree.
     */
    static readonly RUN_CONTRACT_JOIN_ALIAS: string = 'rcr';

    busy: boolean;
    initialized: boolean;

    provisionRunId: number;
    provisionRunDto: ProvisionRunDto;

    /** Active tab; `overview` (the step bar) is the entry point of the page. */
    activeTab: CpRunTabKey = 'overview';

    /**
     * Pre-measurement fallback height of the tab workbenches. Auto-fit overrides it as soon as it
     * has a measurement — see the class TSDoc. Approximates breadcrumb + page header + run header
     * card + tab band above the workbench.
     */
    readonly workbenchViewportOffsetPx: number = 330;

    // ── Worklist widgets, one per entity tab (built ONCE, reloaded in place — R2) ────────────────

    /** `cp.dashboard.run.contractRuns.table` — the contracts processed by this run. */
    contractRunsWidget: WidgetData;
    /** `cp.dashboard.run.provisionContracts.table` — the event time slices behind those contracts. */
    provisionContractsWidget: WidgetData;
    /** `cp.dashboard.run.contractAgents.table` — the commission rate rows of those events. */
    contractAgentsWidget: WidgetData;
    /** `cp.dashboard.run.messages.ql` — contract messages, joined via their ProvisionContractRun. */
    messagesWidget: WidgetData;
    /** `cp.dashboard.run.runMessages.table` — run-/agent-level messages (payout brake, validation). */
    runMessagesWidget: WidgetData;
    /** `cp.dashboard.run.contractTransactions.table` — contract-side bookings of this run. */
    contractTransactionsWidget: WidgetData;
    /** `cp.dashboard.run.agentTransactions.table` — account-side bookings of this run. */
    agentTransactionsWidget: WidgetData;
    /** `cp.dashboard.run.reserveCoverage.table` — reserve coverage incl. shortfallAmount (U-03). */
    reserveCoverageWidget: WidgetData;

    /**
     * The mounted worklist widget INSTANCES. They exist only for `refreshWidget()` — the documented
     * reload-in-place mechanism (see `BdDunningProposalWorkbenchComponent`). Re-assigning the
     * `WidgetData` instead would hand the table a new reference and reset the reviewer's sort,
     * filter and scroll position on every job end.
     */
    @ViewChild('contractRunsWidgetRef') contractRunsWidgetComponent: MvsWidgetComponent;
    @ViewChild('provisionContractsWidgetRef') provisionContractsWidgetComponent: MvsWidgetComponent;
    @ViewChild('contractAgentsWidgetRef') contractAgentsWidgetComponent: MvsWidgetComponent;
    @ViewChild('messagesWidgetRef') messagesWidgetComponent: MvsWidgetComponent;
    @ViewChild('runMessagesWidgetRef') runMessagesWidgetComponent: MvsWidgetComponent;
    @ViewChild('contractTransactionsWidgetRef') contractTransactionsWidgetComponent: MvsWidgetComponent;
    @ViewChild('agentTransactionsWidgetRef') agentTransactionsWidgetComponent: MvsWidgetComponent;
    @ViewChild('reserveCoverageWidgetRef') reserveCoverageWidgetComponent: MvsWidgetComponent;

    /** Selected row per entity tab — drives `[panes]` and the `<mvs-object>` on the right. */
    selection: CpRunTabSelection = {
        contractRuns: null,
        provisionContracts: null,
        contractAgents: null,
        messages: null,
        runMessages: null,
        contractTransactions: null,
        agentTransactions: null,
        reserveCoverage: null
    };

    // ── Review filters (worklist scope) ──────────────────────────────────────────────────────────

    /** "Verträge": all contracts or only the ones in ERROR ("nur Fehler"). */
    contractRunFilter: CpRunContractFilter = 'all';

    readonly contractRunFilterOptions: CpRunFilterOption<CpRunContractFilter>[] = [
        {label: 'Alle', value: 'all'},
        {label: 'Nur Fehler', value: 'errors'}
    ];

    /** "Meldungen": all messages, only errors, or errors and warnings. */
    messageFilter: CpRunMessageFilter = 'all';

    readonly messageFilterOptions: CpRunFilterOption<CpRunMessageFilter>[] = [
        {label: 'Alle', value: 'all'},
        {label: 'Nur Fehler', value: 'errors'},
        {label: 'Fehler und Warnungen', value: 'errorsAndWarnings'}
    ];

    /**
     * Bumped on every job end — the totals strips of the two transaction tabs reload on it, at the
     * same moment the worklists below them are reloaded in place.
     */
    totalsReloadToken: number = 0;

    /** Row counts shown as tab badges (04 §5.3) — one batched QL request, refreshed on job end. */
    counts: CpRunTabCounts = {
        contractRuns: null,
        provisionContracts: null,
        contractAgents: null,
        messages: null,
        runMessages: null,
        contractTransactions: null,
        agentTransactions: null,
        reserveCoverage: null
    };

    private routeSubscription: Subscription;
    private statusSubscription: Subscription;
    private jobWasRunning: boolean = false;
    private jobRunning: boolean = false;

    constructor(
        protected route: ActivatedRoute,
        protected provisionRunService: CpProvisionRunService,
        protected qlService: QlService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {

        this.routeSubscription = this.route.paramMap.subscribe(paramMap => {
            const runId = Number(paramMap.get('provisionRunId'));

            if (!runId || runId === this.provisionRunId) {
                return;
            }

            this.provisionRunId = runId;

            // A different run means different rows behind every tab, so the widgets, the selection
            // and the counters all belong to the previous run and must not survive the switch. This
            // is the ONLY place that discards the widgets - a selection never does (R2).
            this.resetTabState();
            this.refreshComponent();
        });

        this.subscribeToJobCompletion();

        this.initialized = true;
    }

    /**
     * Refresh component — read the run; the counters and the two run-scoped worklists follow from
     * {@link handleProvisionRunLoaded} once the run is there. Worklist widgets are built on demand,
     * per tab, by {@link ensureTabWidget}.
     */
    refreshComponent(): void {
        this.retrieveProvisionRun();
    }

    /**
     * Re-read the run.
     *
     * Called on route change, on EVERY tab change and on every job end: status, and everything the
     * tabs show, are produced while the `cp%` jobs run.
     */
    retrieveProvisionRun(): void {

        if (!this.provisionRunId) {
            return;
        }

        const filter = FilterCriteria.create('id', FilterCriteria.cOperatorEqual, this.provisionRunId);
        const objectRequest = ObjectRequestList.createBasic(false, [filter], []);

        this.provisionRunService.list(objectRequest).subscribe((res: DtoList<ProvisionRunDto>) => {
            if (res.entries.length > 0) {
                this.provisionRunDto = res.entries[0] as ProvisionRunDto;
                this.handleProvisionRunLoaded();
            }
        });
    }

    /**
     * The run is on the table — everything that needs more than its id runs from here.
     *
     * The two tabs added in WP-E14 scope their rows through the run's PROVISION TYPE plus a reverse
     * join to this run's `cp.ProvisionContractRun` (see the run-scope comment above
     * `initProvisionContractsWidget`), so neither their
     * worklist nor their counter can be built from `provisionRunId` alone. Re-entering
     * {@link ensureTabWidget} here is what makes the deep link `/#/cp/dashboard/run/{id}` land on
     * one of those tabs correctly: the first attempt during route resolution finds no run yet and
     * returns, this one builds the widget. It stays a build-ONCE path — `ensureTabWidget` returns
     * early for a tab that already has a widget (R2).
     */
    private handleProvisionRunLoaded(): void {

        this.refreshTabCounts();

        if (this.activeTab !== 'overview') {
            this.ensureTabWidget(this.activeTab);
        }
    }

    /**
     * The step bar re-read the run itself — take its answer over instead of firing a second request.
     *
     * @param provisionRunDto the run as the step bar last read it
     */
    handleProvisionRunDtoChange(provisionRunDto: ProvisionRunDto): void {
        if (provisionRunDto) {
            this.provisionRunDto = provisionRunDto;
            // Same reason as in retrieveProvisionRun(): this may be the FIRST run the component
            // sees, and the two run-scoped tabs cannot be built without it.
            this.ensureTabWidget(this.activeTab === 'overview' ? 'contractRuns' : this.activeTab);
        }
    }

    // ── Worklist widgets ────────────────────────────────────────────────────────────────────────

    /**
     * Build the worklist widget of ONE tab, the first time that tab is opened — and never again.
     *
     * This is the lazy gate AND the "created once" guarantee in one method (see the class TSDoc):
     * the early return on an existing widget is what makes re-entering a tab a no-op, so the table
     * keeps the reviewer's sort, filter and scroll position for the whole life of the page.
     *
     * @param tab the entity tab whose worklist is needed
     */
    ensureTabWidget(tab: CpRunEntityTabKey): void {

        if (!this.provisionRunId) {
            return;
        }

        switch (tab) {
            case 'contractRuns':
                if (!this.contractRunsWidget) {
                    this.initContractRunsWidget();
                }
                return;
            case 'provisionContracts':
                // Needs the run DTO, not just its id (run scope, see initProvisionContractsWidget). The early return is
                // not a lost chance: {@link handleProvisionRunLoaded} re-enters for the active tab
                // the moment the run arrives.
                if (!this.provisionContractsWidget && this.provisionRunDto) {
                    this.initProvisionContractsWidget();
                }
                return;
            case 'contractAgents':
                if (!this.contractAgentsWidget && this.provisionRunDto) {
                    this.initContractAgentsWidget();
                }
                return;
            case 'messages':
                if (!this.messagesWidget) {
                    this.initMessagesWidget();
                }
                return;
            case 'runMessages':
                if (!this.runMessagesWidget) {
                    this.initRunMessagesWidget();
                }
                return;
            case 'contractTransactions':
                if (!this.contractTransactionsWidget) {
                    this.initContractTransactionsWidget();
                }
                return;
            case 'agentTransactions':
                if (!this.agentTransactionsWidget) {
                    this.initAgentTransactionsWidget();
                }
                return;
            case 'reserveCoverage':
                if (!this.reserveCoverageWidget) {
                    this.initReserveCoverageWidget();
                }
                return;
        }
    }

    /**
     * Tab "Verträge": every contract this run touched, with its per-contract outcome.
     *
     * Columns (FE-A item 7): VSNR, Vertragsart, Hauptvermittler, Ergebnis and Fehlergrund — the
     * technical columns (ids, alias, audit fields, run name) are gone. Fehlergrund is the code and
     * text of the ERROR message of that contract run; the full list of messages, the Vermittler-Id
     * and the booked amount live in the read-only detail pane on the right.
     *
     * The two reverse joins (`cm.ContractAgent#contract` restricted to the main role,
     * `cp.ProvisionContractRunMessage#provisionContractRun` restricted to active errors) are STACKED
     * LEFT joins with their restriction in the ON clause — `onlyFirst` was measured to ignore its
     * `filterCriteria` (fe_core_ql interim rule). Consequence, accepted and documented: a contract
     * with SEVERAL Hauptvermittler (M-003) or several active error messages appears once per such
     * row, so the table can be longer than the tab badge. In practice an errored contract run
     * carries one error (the engine dedupes per event, code and text).
     *
     * "Nur Fehler" ({@link contractRunFilter}) narrows the query to `status = ERROR`.
     *
     * Ordering: see the comment at `sortings` below - "errors first" is carried by the joined error
     * message, NOT by the status ordinal (ABORTED(5) would outrank ERROR(4)).
     */
    private initContractRunsWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunContractRuns',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractRun',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'contract',
                        as: 'c',
                        joinType: 'left',
                        fields: [{name: 'contractIdentifier'}],
                        joins: [
                            {
                                type: 'entity',
                                name: 'contractType',
                                as: 'ct',
                                joinType: 'left',
                                fields: [{name: 'name'}]
                            },
                            {
                                type: 'entity',
                                name: 'cm.ContractAgent#contract',
                                as: 'mca',
                                joinType: 'left',
                                fields: [{name: 'id'}],
                                filterCriteria: [
                                    FilterCriteria.create('mca.assignment', FilterCriteria.cOperatorEqual,
                                        ContractAgentEnum.main)
                                ],
                                joins: [{type: 'entity', name: 'agent', as: 'mag', joinType: 'left',
                                    fields: [{name: 'id'}, {name: 'name'}]}]
                            }
                        ]
                    },
                    {
                        type: 'entity',
                        name: 'cp.ProvisionContractRunMessage#provisionContractRun',
                        as: 'err',
                        joinType: 'left',
                        fields: [{name: 'messageCode'}, {name: 'message'}],
                        filterCriteria: [
                            FilterCriteria.create('err.severity', FilterCriteria.cOperatorEqual,
                                ProvisionMessageSeverity.ERROR),
                            FilterCriteria.create('err.obsolete', FilterCriteria.cOperatorEqual, false)
                        ]
                    }
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId),
                // "Nur Fehler" scope: only contract runs the run marked ERROR
                ...(this.contractRunFilter === 'errors'
                    ? [FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual,
                        ProvisionContractRunStatus.ERROR)]
                    : [])
            ],
            // Contracts with an active ERROR message first, then by outcome, then in processing order -
            // the reviewer starts with what blocks a booking. Sorting by `e.status` descending is NOT
            // "errors first": the ordinals are CALCULATED(0) ... ERROR(4), ABORTED(5), so descending
            // puts the ABORTED contract runs of an aborted run in front of the real errors. The sort key
            // `err.messageCode` (the left-joined active ERROR message) sorts ascending with NULLs last in
            // PostgreSQL, i.e. every row carrying an error message precedes every row without one;
            // `e.status` descending then orders the rest, `e.id` keeps the processing order.
            sortings: [new Sorting('err.messageCode', true), new Sorting('e.status', false), new Sorting('e.id', true)]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Verträge" tab - cp.ProvisionContractRun rows of this provision run
         *         with VSNR, Vertragsart, Hauptvermittler, Ergebnis and the Fehlergrund (active ERROR
         *         message of the contract run); optional "nur Fehler" scope; selection opens the
         *         read-only contract-run review on the right.
         */
        this.contractRunsWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.contractRuns.table',
            'Verträge',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm([
                {field: 'c.contractIdentifier', label: 'VSNR'},
                {field: 'ct.name', label: 'Vertragsart'},
                {field: 'mag.name', label: 'Hauptvermittler'},
                {field: 'e.status', label: 'Ergebnis'},
                {field: 'err.messageCode', label: 'Fehlercode'},
                {field: 'err.message', label: 'Fehlergrund', outputControl: 'textShowMore'}
            ]))
        );
    }

    /**
     * Switch the "Verträge" scope (Alle / Nur Fehler).
     *
     * This is a change of the worklist's QUERY, not a selection, so rebuilding its `WidgetData` is
     * the correct move here (R2 forbids it only inside a selection handler). The selection of the
     * tab is cleared because the selected row may no longer be in scope.
     */
    onContractRunFilterChange(filter: CpRunContractFilter): void {
        if (!filter || filter === this.contractRunFilter) {
            return;
        }
        this.contractRunFilter = filter;
        this.selection.contractRuns = null;
        if (this.provisionRunId) {
            this.initContractRunsWidget();
        }
    }

    /*
     * How the two WP-E14 tabs are scoped to "this run" — the contracts THIS run evaluated
     * (projects/cp-assignment-conditions/analysis-points-3-5.md, Frage 5, Option A).
     *
     * Neither `cp.ProvisionContract` (R-07) nor `cp.ProvisionContractAgent` (R-08) carries a
     * `provisionRun` foreign key — by design: an event time slice and its commission rate rows
     * outlive the single run that last wrote them, and are carried forward by every later run
     * (02 §R-07/R-08). What a run DID evaluate is recorded in `cp.ProvisionContractRun`, one row per
     * (run, contract) — and that entity points at the contract, not the other way round.
     *
     * QL reaches it anyway with a REVERSE join `cp.ProvisionContractRun#contract` hung under the
     * existing `contract` join (backend: `MetaService.buildReverseJoinIndexForTenant()` registers
     * every `@ManyToOne` as a `oneToMany` join `<sourceAlias>#<attribute>` on its TARGET entity).
     * The scope is therefore TWO criteria, applied identically to worklist and counter:
     *
     * 1. `provisionTypeId = run's provision type` (the `provisionTypeId` filter) — a contract can be in
     *    scope of several provision types, the run evaluated only its own.
     * 2. an INNER reverse join to `cp.ProvisionContractRun` with `provisionRunId = this run` in its
     *    ON clause (the `cp.ProvisionContractRun#contract` join) — rows of contracts this run did not
     *    evaluate drop out.
     *
     * Why this needs neither a `dependsOn` query nor an `IN` list, and cannot multiply rows:
     * - A `#` join without `onlyFirst` and without `structureType: 'object'` is STACKED into the
     *   same SQL unit as its parent (`QlDpEntityJdbcProviderImpl.stack()` / `storeRegularJoin()`),
     *   so the INNER join filters the parent rows in the database. With `onlyFirst` it would become
     *   a separate child unit and filter nothing — it must not be added.
     * - The join's `filterCriteria` land in the ON clause (`QlDpEntityJdbcProviderImplCompiler`,
     *   "check whether filter criteria exist on join level").
     * - The unique index `cp_provision_contract_run_bkey (provision_run_id, contract_id)` allows at
     *   most ONE matching `ProvisionContractRun` per contract, so each event / rate row appears once
     *   and the `includeCount` of the counter equals the table's row count.
     *
     * Deliberately NOT limited by the run's period: the selection of a run has NO upper date
     * (BR-023/BR-053, 03 §6.1 "Kein oberes Datum"), a run evaluates and books contracts whose event
     * starts AFTER its period end as well, and the valuta of a booking is the event start date — the
     * period only limits the PAYOUT. A former `startDate <= periodEndDate` filter hid exactly those
     * future-dated events, so the tabs contradicted the "Verträge" tab and the transaction tabs.
     *
     *
     * The scope is written INLINE in every query that needs it (worklists of both tabs and their two
     * counters) — one query, one literal (fe_core_ql single-assignment rule).
     */

    /**
     * Tab "Ereignisse" (04-frontend.md §5, tab 2): the `cp.ProvisionContract` event time slices in
     * scope for this run.
     *
     * This is the level at which the run decides WHETHER a contract earns commission at all and on
     * WHICH base: one row per (Vertrag, Provisionstyp, Ereigniszeitpunkt) with its eligibility, its
     * application date and — the three columns that make the E7 difference mode explainable — the
     * old base, the new base and the mode actually applied. Newest event first, because the event
     * an operator is looking for after a run is almost always the one the run just produced.
     */
    private initProvisionContractsWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunProvisionContracts',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContract',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'contract',
                        as: 'c',
                        joinType: 'left',
                        fields: [
                            {name: 'contractIdentifier'},
                            {name: 'alias'}
                        ],
                        // run scope: only contracts THIS run evaluated (see initProvisionContractsWidget)
                        joins: [{
                            type: 'entity',
                            name: 'cp.ProvisionContractRun#contract',
                            as: 'rcr',
                            joinType: 'inner',
                            fields: [{name: 'id'}],
                            filterCriteria: [
                                FilterCriteria.create('rcr.provisionRunId', FilterCriteria.cOperatorEqual,
                                    this.provisionRunId)
                            ]
                        }]
                    },
                    {
                        type: 'entity',
                        name: 'provisionTypeContractEvent',
                        as: 'pce',
                        joinType: 'left',
                        fields: [{name: 'name'}]
                    }
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual,
                this.provisionRunDto.provisionTypeDtoId)],
            sortings: [
                new Sorting('e.startDate', false),
                new Sorting('e.id', false)
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Ereignisse" tab - the cp.ProvisionContract event time slices in
         *         scope for this provision run, with the triggering Provisionsereignis, the event
         *         period, the Antragsdatum, the eligibility and the E7 base triple (old base, new
         *         base, applied mode) that explains how the amount came about.
         */
        this.provisionContractsWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.provisionContracts.table',
            'Ereignisse',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'c.contractIdentifier', label: 'Vertrag'},
                {field: 'pce.name', label: 'Ereignis'},
                {field: 'e.startDate', label: 'Periode ab'},
                {field: 'e.endDate', label: 'Periode bis'},
                {field: 'e.applicationDate', label: 'Antragsdatum'},
                {field: 'e.eligibility', label: 'Anspruch'},
                {field: 'e.eligibilityOverwrite', label: 'Anspruch (Overwrite)'},
                {field: 'e.baseReferenceAmountMonthly', label: 'Basis alt'},
                {field: 'e.baseAmountMonthly', label: 'Basis neu'},
                {field: 'e.appliedBaseCalculationMode', label: 'Basis-Modus'},
                {field: 'e.agentsResolved', label: 'Sätze ermittelt'}
            ]))
        );
    }

    /**
     * Tab "Sätze" (04-frontend.md §5, tab 3): the `cp.ProvisionContractAgent` commission rate rows.
     *
     * One row per (Ereignis × Provisionsart × Berater) — the level the money is actually booked on
     * since WP-E5. The column set follows §5 literally and is what makes a single amount auditable:
     * the calculated `provisionAmount` NEXT TO its manual overwrite and the overwrite's provenance,
     * and the applied liability type NEXT TO the source it came from (assignment result versus an
     * absolute matrix override, A3-05).
     *
     * `contract`, `provisionType` and `provisionTypeContractEvent` are denormalised onto this entity
     * (A6-04) precisely so this selection path needs no detour over R-07; the join on
     * `provisionContract` is here only to display and sort by the event period, which lives on R-07
     * alone — it is not part of the run scope (see the comment above `initProvisionContractsWidget`).
     */
    private initContractAgentsWidget(): void {

        // Joins, in order: `contract` carries the run scope directly (it is denormalised onto R-08, A6-04, and
        // always equals provisionContract.contract); `pc` only displays/sorts by the event period; `pa`/`ag`:
        // "Berater" is am.Agent, not cp.ProvisionAgent (the Abrechnungskonto), so the name sits one join out.
        const qlQuery: QlQueryDto = {
            name: 'cpRunContractAgents',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractAgent',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'contract', as: 'c', joinType: 'left',
                        fields: [{name: 'contractIdentifier'}],
                        joins: [{
                            type: 'entity', name: 'cp.ProvisionContractRun#contract', as: 'rcr', joinType: 'inner',
                            fields: [{name: 'id'}],
                            filterCriteria: [
                                FilterCriteria.create('rcr.provisionRunId', FilterCriteria.cOperatorEqual,
                                    this.provisionRunId)
                            ]
                        }]
                    },
                    {type: 'entity', name: 'provisionContract', as: 'pc', joinType: 'left',
                        fields: [{name: 'startDate'}, {name: 'endDate'}]},
                    {type: 'entity', name: 'provisionTypeContractEvent', as: 'pce', joinType: 'left',
                        fields: [{name: 'name'}]},
                    {type: 'entity', name: 'provisionTypeComponent', as: 'ptc', joinType: 'left',
                        fields: [{name: 'name'}]},
                    {
                        type: 'entity', name: 'provisionAgent', as: 'pa', joinType: 'left',
                        joins: [{type: 'entity', name: 'agent', as: 'ag', joinType: 'left', fields: [{name: 'name'}]}]
                    },
                    {type: 'entity', name: 'liabilityType', as: 'lt', joinType: 'left', fields: [{name: 'name'}]}
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual,
                this.provisionRunDto.provisionTypeDtoId)],
            sortings: [new Sorting('pc.startDate', false), new Sorting('e.id', false)]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Sätze" tab - the cp.ProvisionContractAgent rows in scope for this
         *         provision run (one per Ereignis x Provisionsart x Berater), showing the calculated
         *         amount next to its manual overwrite and provenance and the applied liability type
         *         next to its source, so a booked amount can be audited row by row.
         */
        this.contractAgentsWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.contractAgents.table',
            'Sätze',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'c.contractIdentifier', label: 'Vertrag'},
                {field: 'pce.name', label: 'Ereignis'},
                {field: 'pc.startDate', label: 'Periode ab'},
                {field: 'ptc.name', label: 'Provisionsart'},
                {field: 'ag.name', label: 'Berater'},
                {field: 'e.agentRole', label: 'Rolle'},
                {field: 'e.source', label: 'Quelle'},
                {field: 'e.provisionAmount', label: 'Betrag'},
                {field: 'e.provisionAmountOverwrite', label: 'Overwrite'},
                {field: 'e.overwriteSource', label: 'Overwrite-Quelle'},
                {field: 'lt.name', label: 'Haftungstyp'},
                {field: 'e.appliedLiabilitySource', label: 'Haftungsquelle'},
                {field: 'e.liabilityStatus', label: 'Haftungsstatus'},
                {field: 'e.inLiabilityAmount', label: 'In Haftung'}
            ]))
        );
    }

    /**
     * Tab "Meldungen": contract messages of this run.
     *
     * `cp.ProvisionContractRunMessage` has NO own run reference — it hangs on the
     * `ProvisionContractRun`, so the run filter has to travel over that join (ANF: "joined mit
     * ProvisionContractRun"). The nested `contract` join then names the contract the message is
     * about, which is the whole point of the tab.
     *
     * WP-E14 adds the EREIGNIS column (04 §5, tab 4): since the run works one event time slice at a
     * time, "Vertrag X, Meldung Y" is no longer a unique statement — the same contract can carry one
     * message per event. The message's own (nullable) `provisionContract` reference names the slice,
     * and the Provisionsereignis hangs off it one join further out.
     *
     * ROW FAN-OUT (accepted, documented): the Hauptvermittler join is a stacked LEFT reverse join
     * (`cm.ContractAgent#contract`, role main in its ON clause), so a message whose contract has
     * SEVERAL Hauptvermittler (M-003) comes back once per Hauptvermittler - same message id, a
     * different `mag.*` pair. Consequences: the table can hold more rows than the tab badge (the
     * badge counts messages through a join-free query), the paging counts rows, and the same message
     * can be selected via either of its rows. The detail pane is keyed on the message id and
     * de-duplicates the agents itself (CpRunReviewLookupService.loadContractMessage), so a selection
     * is unambiguous. `onlyFirst` is no alternative: it ignores its own filterCriteria and is
     * defective under the simple pipe (fe_core_ql, interim rule 2026-09-28).
     */
    private initMessagesWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunContractMessages',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractRunMessage',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'provisionContractRun',
                        as: 'pcr',
                        joinType: 'left',
                        fields: [{name: 'status'}],
                        joins: [
                            {
                                type: 'entity',
                                name: 'contract',
                                as: 'c',
                                joinType: 'left',
                                fields: [{name: 'contractIdentifier'}],
                                // Hauptvermittler of the contract (name + Vermittler-Id), see
                                // mainAgentJoin(); the agent a message NAMES (M-004) is in the
                                // "Detailmeldung" column and resolved in the detail pane.
                                joins: [{
                                    type: 'entity',
                                    name: 'cm.ContractAgent#contract',
                                    as: 'mca',
                                    joinType: 'left',
                                    fields: [{name: 'id'}],
                                    filterCriteria: [
                                        FilterCriteria.create('mca.assignment', FilterCriteria.cOperatorEqual,
                                            ContractAgentEnum.main)
                                    ],
                                    joins: [{type: 'entity', name: 'agent', as: 'mag', joinType: 'left',
                                        fields: [{name: 'id'}, {name: 'name'}]}]
                                }]
                            }
                        ]
                    },
                    {
                        type: 'entity',
                        name: 'provisionContract',
                        as: 'pc',
                        joinType: 'left',
                        fields: [{name: 'startDate'}],
                        joins: [
                            {
                                type: 'entity',
                                name: 'provisionTypeContractEvent',
                                as: 'pce',
                                joinType: 'left',
                                fields: [{name: 'name'}]
                            }
                        ]
                    }
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [
                FilterCriteria.create('pcr.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId),
                // Severity scope of the "Meldungen" tab (messageFilter): all / errors / errors and warnings
                ...(this.messageFilter === 'errors'
                    ? [FilterCriteria.create('e.severity', FilterCriteria.cOperatorEqual,
                        ProvisionMessageSeverity.ERROR)]
                    : []),
                ...(this.messageFilter === 'errorsAndWarnings'
                    ? [FilterCriteria.create('e.severity', FilterCriteria.cOperatorIn,
                        [ProvisionMessageSeverity.ERROR, ProvisionMessageSeverity.WARNING])]
                    : [])
            ],
            // Severity descending puts ERROR(2) before WARNING(1) before INFO(0) - the reviewer works
            // the list from the worst message down, which is the point of a message queue. Inside
            // one severity the messages are grouped by code, so all M-026 cases sit together.
            sortings: [
                new Sorting('e.severity', false),
                new Sorting('e.messageCode', true),
                new Sorting('e.createdDate', false)
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Meldungen" tab - cp.ProvisionContractRunMessage rows reached through
         *         their ProvisionContractRun (the entity carries no own run FK), severest first, with
         *         VSNR, the contract's Hauptvermittler (name + Vermittler-Id), the detailed message
         *         and a German timestamp; optional severity scope; selection opens the read-only
         *         message review with the "Was tun?" guidance on the right.
         */
        this.messagesWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.messages.ql',
            'Meldungen',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm([
                {field: 'e.severity', label: 'Schweregrad'},
                {field: 'e.messageCode', label: 'Code'},
                {field: 'c.contractIdentifier', label: 'Vertrag (VSNR)'},
                {field: 'mag.name', label: 'Hauptvermittler'},
                {field: 'mag.id', label: 'Vermittler-Id', outputControl: 'text'},
                {field: 'e.message', label: 'Meldung', outputControl: 'textShowMore'},
                {field: 'e.detailedMessage', label: 'Detailmeldung', outputControl: 'textShowMore'},
                {field: 'pce.name', label: 'Ereignis'},
                {field: 'pc.startDate', label: 'Periode ab'},
                {field: 'e.obsolete', label: 'Veraltet'},
                {field: 'e.createdDate', label: 'Erstellt am', outputControl: 'dateTime'}
            ]))
        );
    }

    /**
     * Switch the "Meldungen" scope (Alle / Nur Fehler / Fehler und Warnungen) — a query change, so
     * the worklist is rebuilt (see {@link onContractRunFilterChange}).
     */
    onMessageFilterChange(filter: CpRunMessageFilter): void {
        if (!filter || filter === this.messageFilter) {
            return;
        }
        this.messageFilter = filter;
        this.selection.messages = null;
        if (this.provisionRunId) {
            this.initMessagesWidget();
        }
    }

    /**
     * Tab "Lauf-Meldungen": messages about the run itself and about single agents — the payout brake
     * (M-012), the start validation (M-019), valuta monotonicity (M-013), inactive agent (M-018).
     */
    private initRunMessagesWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunMessages',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionRunMessage',
                as: 'e',
                joins: [
                    {
                        // "Berater" is am.Agent — cp.ProvisionAgent has no name column of its own.
                        type: 'entity',
                        name: 'provisionAgent',
                        as: 'pa',
                        joinType: 'left',
                        fields: [{name: 'id'}],
                        joins: [
                            {
                                type: 'entity',
                                name: 'agent',
                                as: 'ag',
                                joinType: 'left',
                                fields: [{name: 'id'}, {name: 'name'}]
                            }
                        ]
                    }
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId)
            ],
            sortings: [
                new Sorting('e.severity', false),
                new Sorting('e.createdDate', false)
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Lauf-Meldungen" tab - cp.ProvisionRunMessage rows of this run
         *         (payout brake, start validation, valuta monotonicity), severest first, curated to
         *         the business columns (Berater name + Vermittler-Id, detail, German timestamp)
         *         instead of ~22 technical ones.
         */
        this.runMessagesWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.runMessages.table',
            'Lauf-Meldungen',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm([
                {field: 'e.severity', label: 'Schweregrad'},
                {field: 'e.messageCode', label: 'Code'},
                {field: 'ag.name', label: 'Berater'},
                {field: 'ag.id', label: 'Vermittler-Id', outputControl: 'text'},
                {field: 'e.message', label: 'Meldung', outputControl: 'textShowMore'},
                {field: 'e.detailedMessage', label: 'Detailmeldung', outputControl: 'textShowMore'},
                {field: 'e.obsolete', label: 'Veraltet'},
                {field: 'e.createdDate', label: 'Erstellt am', outputControl: 'dateTime'}
            ]))
        );
    }

    /**
     * Tab "Vertrags-Transaktionen": the contract-side bookings this run produced.
     *
     * WP-E14 adds the three columns that say WHAT a booking pays for (04 §5, tab 6): Ereignis,
     * Provisionsart and Berater. All three hang off `provisionContractAgent` — the reconcile anchor
     * R-09 gained in A6-01 — because since WP-E5 a booking belongs to one commission rate row, not
     * to a contract as a whole.
     */
    private initContractTransactionsWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunContractTransactions',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractTransaction',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'contractTransactionType',
                        as: 'ctt',
                        joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity',
                        name: 'provisionContractAgent',
                        as: 'pca',
                        joinType: 'left',
                        joins: [
                            {
                                type: 'entity',
                                name: 'contract',
                                as: 'c',
                                joinType: 'left',
                                fields: [{name: 'contractIdentifier'}]
                            },
                            {
                                type: 'entity',
                                name: 'provisionTypeContractEvent',
                                as: 'pce',
                                joinType: 'left',
                                fields: [{name: 'name'}]
                            },
                            {
                                type: 'entity',
                                name: 'provisionTypeComponent',
                                as: 'ptc',
                                joinType: 'left',
                                fields: [{name: 'name'}]
                            },
                            {
                                type: 'entity',
                                name: 'provisionAgent',
                                as: 'pa',
                                joinType: 'left',
                                joins: [
                                    {
                                        type: 'entity',
                                        name: 'agent',
                                        as: 'ag',
                                        joinType: 'left',
                                        fields: [{name: 'name'}]
                                    }
                                ]
                            }
                        ]
                    }
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId)
            ],
            sortings: [new Sorting('e.valutaDate', false)]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Vertrags-Transaktionen" tab - cp.ProvisionContractTransaction rows
         *         booked by this run, newest valuta first, with the booking type named and the
         *         Ereignis, Provisionsart and Berater the booking pays for resolved over the
         *         reconcile anchor provisionContractAgent.
         */
        this.contractTransactionsWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.contractTransactions.table',
            'Vertrags-Transaktionen',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'c.contractIdentifier', label: 'Vertrag'},
                {field: 'pce.name', label: 'Ereignis'},
                {field: 'ptc.name', label: 'Provisionsart'},
                {field: 'ag.name', label: 'Berater'},
                {field: 'ctt.name', label: 'Buchungsart'},
                {field: 'e.amount', label: 'Betrag'},
                {field: 'e.valutaDate', label: 'Valuta'},
                {field: 'e.status', label: 'Status'},
                {field: 'e.event', label: 'Buchungsanlass'},
                {field: 'e.referenceText', label: 'Referenz'}
            ]))
        );
    }

    /**
     * Tab "Konto-Transaktionen": the account-side counterparts on the agents' provision accounts.
     *
     * ── WHY THERE IS NO "PROVISIONSART" COLUMN HERE (04 §5, tab 7 — DELIBERATELY NOT IMPLEMENTED)
     *
     * §5 asks for a Provisionsart column "über das R-09-Gegenstück". That is not expressible from
     * this root: the 1:1 pairing between an account booking (R-04) and its contract booking (R-09)
     * is held ONE-SIDEDLY on the contract side — 02 §R-04 states it in as many words ("Kein FK auf
     * `ProvisionContractTransaction` — die Paar-Verknüpfung liegt einseitig auf der Vertragsseite"),
     * so `cp.ProvisionAgentTransaction` has no forward join to its pair.
     *
     * The pair IS reachable through the reverse join `cp.ProvisionContractTransaction#agentTransaction`
     * (1:1 — `cp_provision_contract_transaction_bkey` is unique on `agent_transaction_id`, so no
     * `onlyFirst`), and from there `provisionContractAgent.provisionTypeComponent`. Adding that column
     * is a separate change that has not been made yet. Rooting
     * the tab on R-09 instead was rejected — it would silently DROP every manual account booking
     * (`provisionRun = NULL`, no R-09 counterpart), which is exactly the row an operator opens this
     * tab for. The Provisionsart of a booking is available one tab to the left, on "Sätze".
     */
    private initAgentTransactionsWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunAgentTransactions',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgentTransaction',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'transactionType',
                        as: 'tt',
                        joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity',
                        name: 'provisionAgent',
                        as: 'pa',
                        joinType: 'left',
                        joins: [
                            {
                                type: 'entity',
                                name: 'agent',
                                as: 'ag',
                                joinType: 'left',
                                fields: [{name: 'name'}]
                            }
                        ]
                    }
                ],
                parameters: [{param: 'resolve_fkey_texts', value: 'true'}]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId)
            ],
            sortings: [new Sorting('e.valutaDate', false)]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Konto-Transaktionen" tab - cp.ProvisionAgentTransaction rows booked
         *         by this run onto the Abrechnungskonten of the Berater, newest valuta first, with the
         *         Berater name resolved over the Abrechnungskonto.
         */
        this.agentTransactionsWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.agentTransactions.table',
            'Konto-Transaktionen',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'ag.name', label: 'Berater'},
                {field: 'tt.name', label: 'Buchungsart'},
                {field: 'e.amount', label: 'Betrag'},
                {field: 'e.valutaDate', label: 'Valuta'},
                {field: 'e.bookingDate', label: 'Buchungsdatum'},
                {field: 'e.status', label: 'Status'},
                {field: 'e.event', label: 'Buchungsanlass'},
                {field: 'e.referenceText', label: 'Referenz'}
            ]))
        );
    }

    /**
     * Tab "Deckung": reserve coverage per agent for this run.
     *
     * Sorted by `shortfallAmount` DESCENDING on purpose: a shortfall is the payout-brake indicator
     * (U-03), so the agents who will be held back by the brake are the first rows of the list rather
     * than something the operator has to go looking for.
     */
    private initReserveCoverageWidget(): void {

        const qlQuery: QlQueryDto = {
            name: 'cpRunReserveCoverage',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgentReserveCoverage',
                as: 'e',
                joins: [
                    {
                        // Berater name: two hops, cp.ProvisionAgent has no name column of its own
                        // (same reasoning as buildCpProvisionAgentGroupQuery).
                        type: 'entity',
                        name: 'provisionAgent',
                        as: 'pa',
                        joinType: 'left',
                        fields: [{name: 'id'}, {name: 'reservePercentage'}],
                        joins: [
                            {
                                type: 'entity',
                                name: 'agent',
                                as: 'ag',
                                joinType: 'left',
                                fields: [{name: 'id'}, {name: 'name'}]
                            }
                        ]
                    }
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId)
            ],
            sortings: [new Sorting('e.shortfallAmount', false)]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of the "Deckung" tab - cp.ProvisionAgentReserveCoverage rows of this run
         *         with the Berater NAME and Vermittler-Id (instead of bare account ids) and the four
         *         coverage figures under plain German headers, largest Unterdeckung first.
         */
        this.reserveCoverageWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.run.reserveCoverage.table',
            'Deckung',
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm([
                {field: 'ag.name', label: 'Berater'},
                {field: 'ag.id', label: 'Vermittler-Id', outputControl: 'text'},
                {field: 'e.openLiabilityAmount', label: 'Offene Haftung'},
                {field: 'e.reserveBalance', label: 'Rücklage (Saldo)'},
                {field: 'e.shortfallAmount', label: 'Unterdeckung (Haftung ohne Rücklage)'},
                {field: 'e.coverageQuote', label: 'Deckungsquote'},
                {field: 'pa.reservePercentage', label: 'Rücklagensatz (%)'}
            ]))
        );
    }

    // ── Tab counters ────────────────────────────────────────────────────────────────────────────

    /**
     * Count the rows behind every tab in ONE batched QL request (04 §5.3).
     *
     * Every query is paged to a single row and asks for `includeCount`, so the answer is the total
     * count without transporting the rows — the same shape the pd dashboard and the cc mention
     * indicator use. EIGHT pipes travel in one round-trip, so the badges cost one request per run
     * load / job end, not eight.
     *
     * Called only from {@link handleProvisionRunLoaded}, because the two WP-E14 tabs are counted
     * through the run's provision type in addition to the run id (see the run-scope comment above
     * `initProvisionContractsWidget`).
     */
    refreshTabCounts(): void {

        if (!this.provisionRunId || !this.provisionRunDto) {
            return;
        }

        this.qlService.builder()
            .query({
                name: 'contractRunsCount',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionContractRun', as: 'e'},
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual,
                    this.provisionRunId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                // Same run scope as the worklist: provision type + the INNER reverse join to this
                // run's ProvisionContractRun under the contract join (see initProvisionContractsWidget).
                name: 'provisionContractsCount',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContract',
                    as: 'e',
                    joins: [{
                        type: 'entity', name: 'contract', as: 'c', joinType: 'left', fields: [{name: 'id'}],
                        joins: [{
                            type: 'entity',
                            name: 'cp.ProvisionContractRun#contract',
                            as: 'rcr',
                            joinType: 'inner',
                            fields: [{name: 'id'}],
                            filterCriteria: [
                                FilterCriteria.create('rcr.provisionRunId', FilterCriteria.cOperatorEqual,
                                    this.provisionRunId)
                            ]
                        }]
                    }]
                },
                filters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual,
                    this.provisionRunDto.provisionTypeDtoId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                // Same run scope as the worklist. The worklist's other left joins are many-to-one
                // and never change the count, so the counter carries only the scope join.
                name: 'contractAgentsCount',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContractAgent',
                    as: 'e',
                    joins: [{
                        type: 'entity', name: 'contract', as: 'c', joinType: 'left', fields: [{name: 'id'}],
                        joins: [{
                            type: 'entity',
                            name: 'cp.ProvisionContractRun#contract',
                            as: 'rcr',
                            joinType: 'inner',
                            fields: [{name: 'id'}],
                            filterCriteria: [
                                FilterCriteria.create('rcr.provisionRunId', FilterCriteria.cOperatorEqual,
                                    this.provisionRunId)
                            ]
                        }]
                    }]
                },
                filters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual,
                    this.provisionRunDto.provisionTypeDtoId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                // Same join reasoning as the widget: the message hangs on the ProvisionContractRun.
                name: 'messagesCount',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContractRunMessage',
                    as: 'e',
                    joins: [{type: 'entity', name: 'provisionContractRun', as: 'pcr', joinType: 'left'}]
                },
                filters: [FilterCriteria.create('pcr.provisionRunId', FilterCriteria.cOperatorEqual,
                    this.provisionRunId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'runMessagesCount',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionRunMessage', as: 'e'},
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual,
                    this.provisionRunId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'contractTransactionsCount',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionContractTransaction', as: 'e'},
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual,
                    this.provisionRunId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'agentTransactionsCount',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionAgentTransaction', as: 'e'},
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual,
                    this.provisionRunId)],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'reserveCoverageCount',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionAgentReserveCoverage', as: 'e'},
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual,
                    this.provisionRunId)],
                paging: PagingDto.createSize(1)
            })
            .pipe({name: 'contractRunsCount', simpleQuery: 'contractRunsCount', settings: {includeCount: true}})
            .pipe({
                name: 'provisionContractsCount',
                simpleQuery: 'provisionContractsCount',
                settings: {includeCount: true}
            })
            .pipe({
                name: 'contractAgentsCount',
                simpleQuery: 'contractAgentsCount',
                settings: {includeCount: true}
            })
            .pipe({name: 'messagesCount', simpleQuery: 'messagesCount', settings: {includeCount: true}})
            .pipe({name: 'runMessagesCount', simpleQuery: 'runMessagesCount', settings: {includeCount: true}})
            .pipe({
                name: 'contractTransactionsCount',
                simpleQuery: 'contractTransactionsCount',
                settings: {includeCount: true}
            })
            .pipe({
                name: 'agentTransactionsCount',
                simpleQuery: 'agentTransactionsCount',
                settings: {includeCount: true}
            })
            .pipe({name: 'reserveCoverageCount', simpleQuery: 'reserveCoverageCount', settings: {includeCount: true}})
            .execute()
            .subscribe(result => {
                this.counts = {
                    contractRuns: result.getCount('contractRunsCount'),
                    provisionContracts: result.getCount('provisionContractsCount'),
                    contractAgents: result.getCount('contractAgentsCount'),
                    messages: result.getCount('messagesCount'),
                    runMessages: result.getCount('runMessagesCount'),
                    contractTransactions: result.getCount('contractTransactionsCount'),
                    agentTransactions: result.getCount('agentTransactionsCount'),
                    reserveCoverage: result.getCount('reserveCoverageCount')
                };
            });
    }

    // ── Tab + selection handling ────────────────────────────────────────────────────────────────

    /**
     * Re-read the run and re-count the tabs on EVERY tab change.
     *
     * Existing widgets are deliberately NOT rebuilt here — {@link ensureTabWidget} returns early for
     * a tab that already has one, because rebuilding would hand its table a new `WidgetData` and
     * reset the reviewer's sort/scroll for nothing. Data that changed in the meantime arrives
     * through the job-end reload ({@link reloadTabWidgets}).
     *
     * Bound as `[value]` + `(valueChange)` rather than as the banana-in-a-box `[(value)]`: the
     * PrimeNG model signal is typed `string | number | undefined`, so a two-way binding would write
     * that union back into the narrowly typed {@link activeTab}. The explicit handler keeps the
     * template honest and does the narrowing in ONE place.
     *
     * @param value the tab value PrimeNG switched to
     */
    onTabChange(value: any): void {
        this.activeTab = value as CpRunTabKey;

        if (this.activeTab !== 'overview') {
            this.ensureTabWidget(this.activeTab);
        }

        // Re-counts the tabs and, for the two run-scoped tabs, builds the worklist that the call
        // above could not build yet — both through handleProvisionRunLoaded().
        this.retrieveProvisionRun();
    }

    /**
     * A row was selected in one of the six worklists — the ONLY thing that happens is that the id is
     * recorded. `[panes]` reads it (1 -> 2) and `<mvs-object>` renders the row on the right (R4).
     * The worklist widget is untouched (R2).
     *
     * @param tab the entity tab the selection came from
     * @param event the widget's selection payload
     */
    handleTabSelect(tab: CpRunEntityTabKey, event: any): void {

        const objectId = this.extractRowId(event);

        // The table emits onObjectSelect only from a row click and has no deselect emission, so a
        // missing id is a malformed payload, never an intentional deselect. Guarding instead of
        // clearing keeps the detail pane from blanking out on something we cannot interpret.
        if (!objectId) {
            return;
        }

        this.selection[tab] = objectId;
    }

    /**
     * Clear the selection of one tab — collapses its workbench back to one pane.
     *
     * @param tab the entity tab to clear
     */
    clearTabSelection(tab: CpRunEntityTabKey): void {
        this.selection[tab] = null;
    }

    // ──────────────────────────────────────────────────────────────────────────────────────────
    // "Berechnete Vermittler neu ermitteln" on the selected event row (§3.6, FD-7, D-5)
    // ──────────────────────────────────────────────────────────────────────────────────────────

    /**
     * Visibility of the calculated-agent reset dialog for the row selected in the "Ereignisse" tab.
     *
     * The action lives on the DETAIL side of that tab, not on the list side, because its scope is
     * exactly one event row per call (§3.6 a) — there is no bulk endpoint, and offering the button
     * next to a multi-row table would suggest one. The second entry point is the CR-033 worklist of
     * the configuration check ({@link CpConfigCheckPanelComponent}).
     */
    calculatedAgentResetVisible: boolean = false;

    /**
     * um right `cp_agentChange` for that action (AX-08, no new right) — optimistic default, demoted
     * once the server answers a 403 from inside the dialog. The reasoning for the optimistic
     * polarity is kept in one place, {@link CpFeatureRight}.
     */
    canAgentChange: boolean = true;

    /** Opens the guided reset for the event row currently selected in the "Ereignisse" tab. */
    openCalculatedAgentReset(): void {
        if (!this.selection.provisionContracts) {
            return;
        }
        this.calculatedAgentResetVisible = true;
    }

    /**
     * A completed reset only clears a flag on the event row — nothing is booked and no rate row is
     * added, removed or changed until the next calculation run. Reloading the two tables that show
     * the row (events and their rate rows) keeps the flag column honest without pretending that
     * anything else moved.
     */
    handleCalculatedAgentReset(): void {
        this.provisionContractsWidgetComponent?.refreshWidget();
        this.contractAgentsWidgetComponent?.refreshWidget();
    }

    /**
     * The server refused `cp_agentChange` with a 403 — demote the mirrored flag so the tab stops
     * offering an action the server refuses. Sticky for this component instance; only a 403 reaches
     * here, the dialog filters every other error out.
     */
    handleCalculatedAgentResetForbidden(): void {
        this.canAgentChange = false;
    }

    /** German reason shown on the disabled reset button after a demotion. */
    get agentChangeDeniedMessage(): string {
        return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
    }

    /**
     * Read the row id out of a widget selection payload.
     *
     * A QL-backed table emits `ObjectIdentifierData` whose `objectId` is already the numeric id of
     * the row (`MvsWidgetSpecialBaseComponent.getIdFieldName()` resolves to `e.id` for a QL source).
     * The raw row is kept as a fallback for the shapes that carry the id only inside `data`.
     *
     * @param event the widget's selection payload
     */
    private extractRowId(event: any): number | null {
        const raw = event?.objectId ?? event?.data?.dto?.['e.id'] ?? event?.data?.['e.id'];
        const objectId = Number(raw);
        return Number.isFinite(objectId) && objectId > 0 ? objectId : null;
    }

    /**
     * Discard everything that belongs to the previously shown run.
     */
    private resetTabState(): void {
        this.activeTab = 'overview';
        this.contractRunFilter = 'all';
        this.messageFilter = 'all';
        this.provisionRunDto = null;
        this.contractRunsWidget = null;
        this.provisionContractsWidget = null;
        this.contractAgentsWidget = null;
        this.messagesWidget = null;
        this.runMessagesWidget = null;
        this.contractTransactionsWidget = null;
        this.agentTransactionsWidget = null;
        this.reserveCoverageWidget = null;
        this.selection = {
            contractRuns: null,
            provisionContracts: null,
            contractAgents: null,
            messages: null,
            runMessages: null,
            contractTransactions: null,
            agentTransactions: null,
            reserveCoverage: null
        };
        this.counts = {
            contractRuns: null,
            provisionContracts: null,
            contractAgents: null,
            messages: null,
            runMessages: null,
            contractTransactions: null,
            agentTransactions: null,
            reserveCoverage: null
        };
    }

    // ── Auto refresh on job end ─────────────────────────────────────────────────────────────────

    /**
     * Subscribe to the jb indicator and refresh everything on the transition running -> not-running
     * (04-frontend.md §4 — the tab counters are named there explicitly).
     *
     * The step bar keeps its own subscription for the same signal; both are cheap observers on one
     * BehaviorSubject and each owns a different consequence — the bar re-reads the run to redraw its
     * steps, this component re-counts the tabs and reloads the mounted worklists in place.
     */
    private subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.retrieveProvisionRun();
                this.reloadTabWidgets();
            }
        });
    }

    /**
     * Reload every MOUNTED worklist IN PLACE (R2).
     *
     * Only tabs that were opened at least once have a component instance; the rest are `undefined`
     * and will simply load current data when they are first opened.
     */
    private reloadTabWidgets(): void {
        this.contractRunsWidgetComponent?.refreshWidget();
        this.provisionContractsWidgetComponent?.refreshWidget();
        this.contractAgentsWidgetComponent?.refreshWidget();
        this.messagesWidgetComponent?.refreshWidget();
        this.runMessagesWidgetComponent?.refreshWidget();
        this.contractTransactionsWidgetComponent?.refreshWidget();
        this.agentTransactionsWidgetComponent?.refreshWidget();
        this.reserveCoverageWidgetComponent?.refreshWidget();
        // the totals strips above the two transaction worklists follow the same reload
        this.totalsReloadToken++;
    }

    // ── Header presentation ─────────────────────────────────────────────────────────────────────

    /** German label of the run status. */
    getStatusLabel(status: ProvisionRunStatus): string {
        return getProvisionRunStatusLabel(status);
    }

    /** PrimeNG tag severity of the run status. */
    getStatusSeverity(status: ProvisionRunStatus): CpRunStatusSeverity {
        return getProvisionRunStatusSeverity(status);
    }

    /** True while a background job is producing the data of this run (transient IN_* status). */
    isProcessing(status: ProvisionRunStatus): boolean {
        return isProvisionRunProcessing(status);
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.routeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
